/**
 * report.js — the phone telling us it went wrong.
 *
 * ⚠️ EVERY GUEST-SIDE FAILURE WAS INVISIBLE BEFORE THIS. A denied camera, an
 * upload that died on festival wifi, a poll that never resolved, a browser
 * closed at forty seconds — all of it looked identical to a guest who simply
 * changed their mind. SceneTwist's doctrine says to ASSUME weak cellular,
 * overloaded wifi, abandonment and heat. You cannot assume them and also not
 * measure them.
 *
 * ⚠️ THIS FILE MUST NEVER BREAK THE BOOTH. It is error handling; error handling
 * that can throw is worse than none, because it fails at exactly the moment
 * something else has already gone wrong. Every path here swallows, and the only
 * consequence of a broken reporter is a missing row.
 */
import { getBoothToken } from './boothSession';

/* ⚠️ NEVER REPORT A FAILURE OF THE REPORTER. One 500 from /api/report would
   otherwise become a loop that fills the table with its own noise while the
   original failure scrolls away. */
const SELF = '/api/report';

/* A phone with no signal fails every call in the same second. Send the first
   of each kind and count the rest. */
const seen = new Map();
const BURST_MS = 30000;

function firstOfItsKind(key) {
  const now = Date.now();
  const last = seen.get(key);
  if (last && now - last < BURST_MS) return false;
  seen.set(key, now);
  return true;
}

export async function report(stage, message, extra = {}) {
  try {
    const key = `${stage}:${String(message).slice(0, 80)}`;
    if (!firstOfItsKind(key)) return;

    const token = await getBoothToken().catch(() => null);
    /* No token means no booth session, which means this is not a guest failing
       inside an event — it is the marketing page, and it is not ours to log. */
    if (!token) return;

    await fetch(SELF, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        stage,
        message: String(message).slice(0, 500),
        job_id: extra.jobId ?? null,
        elapsed_ms: extra.elapsedMs ?? null,
        /* ⚠️ WHAT THE PHONE IS, NOT WHO IS HOLDING IT. Screen, browser and
           connection answer "why did it fail for them and not for me"; nothing
           here identifies a person, and consent (`Q-18`) governs anything that
           would. */
        client: {
          screen: typeof window !== 'undefined' ? `${window.screen?.width}x${window.screen?.height}` : null,
          dpr: typeof window !== 'undefined' ? window.devicePixelRatio : null,
          lang: typeof navigator !== 'undefined' ? navigator.language : null,
          net: typeof navigator !== 'undefined' && navigator.connection
            ? {
              type: navigator.connection.effectiveType ?? null,
              down: navigator.connection.downlink ?? null,
              rtt: navigator.connection.rtt ?? null,
              saveData: navigator.connection.saveData ?? null,
            }
            : null,
          ...extra.client,
        },
      }),
      keepalive: true, /* so a report survives the guest closing the tab */
    });
  } catch {
    /* Deliberate. See the file header. */
  }
}

let installed = false;

/**
 * Catch what nobody remembered to catch.
 *
 * ⚠️ THE CLIPPED-REVEAL BUG IS THE CASE THIS EXISTS FOR — four portraits
 * rendered into a region with `overflow:hidden` and not one word of text, no
 * error anywhere, found only because a person looked at it. A window-level
 * handler would not have caught that one either; it catches its louder
 * cousins — the render that throws, the promise nobody awaited — and those are
 * the ones that currently vanish.
 */
export function installGlobalReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    if (!e?.message) return;
    report('render', e.message, {
      client: { at: `${e.filename || '?'}:${e.lineno || 0}` },
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const m = e?.reason?.message || String(e?.reason || 'unhandled rejection');
    report('render', m);
  });
}

export default report;
